import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-md border text-xs font-medium whitespace-nowrap min-w-0 max-w-full transition-[color,box-shadow,background-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3 [&>svg]:shrink-0 data-[interactive=true]:cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border-transparent data-[interactive=true]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground border-transparent data-[interactive=true]:hover:bg-sky-200",
        outline:
          "bg-background text-foreground border-border data-[interactive=true]:hover:bg-neutral-50",
        destructive:
          "bg-red-50 text-destructive border-red-100 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 data-[interactive=true]:hover:bg-red-100",
        success:
          "bg-success-50 text-success-800 border-success-100 data-[interactive=true]:hover:bg-success-100",
        warning:
          "bg-warning-50 text-warning-800 border-warning-100 data-[interactive=true]:hover:bg-warning-100",
        ghost:
          "border-transparent text-foreground data-[interactive=true]:hover:bg-neutral-100",
        link: "text-primary border-transparent underline-offset-4 data-[interactive=true]:hover:underline",
        "color-slate":
          "bg-slate-50 text-slate-800 border-slate-100 data-[interactive=true]:hover:bg-slate-100",
        "color-gray":
          "bg-gray-50 text-gray-800 border-gray-100 data-[interactive=true]:hover:bg-gray-100",
        "color-zinc":
          "bg-zinc-50 text-zinc-800 border-zinc-100 data-[interactive=true]:hover:bg-zinc-100",
        "color-orange":
          "bg-orange-50 text-orange-800 border-orange-100 data-[interactive=true]:hover:bg-orange-100",
        "color-amber":
          "bg-warning-50 text-warning-800 border-warning-100 data-[interactive=true]:hover:bg-warning-100",
        "color-yellow":
          "bg-yellow-50 text-yellow-800 border-yellow-100 data-[interactive=true]:hover:bg-yellow-100",
        "color-lime":
          "bg-lime-50 text-lime-800 border-lime-100 data-[interactive=true]:hover:bg-lime-100",
        "color-emerald":
          "bg-emerald-50 text-emerald-800 border-emerald-100 data-[interactive=true]:hover:bg-emerald-100",
        "color-teal":
          "bg-teal-50 text-teal-800 border-teal-100 data-[interactive=true]:hover:bg-teal-100",
        "color-cyan":
          "bg-cyan-50 text-cyan-800 border-cyan-100 data-[interactive=true]:hover:bg-cyan-100",
        "color-sky":
          "bg-sky-50 text-sky-800 border-sky-100 data-[interactive=true]:hover:bg-sky-100",
        "color-blue":
          "bg-blue-50 text-blue-800 border-blue-100 data-[interactive=true]:hover:bg-blue-100",
        "color-indigo":
          "bg-indigo-50 text-indigo-800 border-indigo-100 data-[interactive=true]:hover:bg-indigo-100",
        "color-violet":
          "bg-violet-50 text-violet-800 border-violet-100 data-[interactive=true]:hover:bg-violet-100",
        "color-purple":
          "bg-purple-50 text-purple-800 border-purple-100 data-[interactive=true]:hover:bg-purple-100",
        "color-fuchsia":
          "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100 data-[interactive=true]:hover:bg-fuchsia-100",
        "color-pink":
          "bg-pink-50 text-pink-800 border-pink-100 data-[interactive=true]:hover:bg-pink-100",
        "color-rose":
          "bg-rose-50 text-rose-800 border-rose-100 data-[interactive=true]:hover:bg-rose-100",
      },
      size: {
        md: "gap-1.5 px-2 py-0.5",
        sm: "h-5 min-w-5 gap-1 px-1",
        xs: "h-4 min-w-4 gap-0.5 px-0.5 [&>svg]:size-2.5!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    isCompact?: boolean;
    /**
     * Ellipsising a label takes two things, because `text-overflow` only paints
     * on a box whose `overflow` isn't `visible`, and loose text inside a flex
     * row is an anonymous box no selector can reach:
     *
     *   1. This prop, which lets the pill itself collapse and clip.
     *   2. `data-label` on the child holding the text, which gives that text a
     *      box of its own to ellipsise in. A plain string child gets one
     *      automatically, so it truncates at the pill's `max-w-full` even
     *      without this prop.
     *
     *     <Badge ellipsis>
     *       <AlertIcon />
     *       <span data-label>Reconnect needed</span>
     *     </Badge>
     *
     * Mark only the text. Icons and trailing chevrons stay unmarked siblings,
     * so they keep the flex `gap` and survive while the label shortens —
     * inside the marked box they would be clipped away first.
     */
    ellipsis?: boolean;
  };

function Badge({
  className,
  variant = "default",
  size,
  isCompact,
  asChild = false,
  ellipsis = false,
  onClick,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";
  const interactive = Boolean(onClick);
  const resolvedSize = size ?? (isCompact ? "sm" : "md");

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-compact={resolvedSize !== "md" || undefined}
      data-interactive={interactive || undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      className={cn(
        badgeVariants({ variant, size: resolvedSize }),
        "[&>[data-label]]:truncate",
        ellipsis && "shrink overflow-hidden",
        className
      )}
      {...props}
    >
      {typeof children === "string" && !asChild ? (
        <span data-label>{children}</span>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Badge, badgeVariants, type BadgeProps };
